import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// Function to generate a simple session ID
function generateSessionId() {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

// Function to convert Markdown links to HTML
function convertMarkdownLinks(text) {
  console.log('Original text:', JSON.stringify(text));
  
  // Handle multiple possible formats
  let result = text;
  
  // Standard markdown: [text](url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  result = result.replace(markdownLinkRegex, (match, linkText, url) => {
    console.log('Found markdown link:', { match, linkText, url });
    const cleanUrl = url.trim();
    return `<a href="${cleanUrl}" target="_blank" style="color:#E63946; font-weight:600; text-decoration:underline;">${linkText}</a>`;
  });
  
  // Also handle case where there might be a period after the closing parenthesis
  const markdownLinkWithPeriodRegex = /\[([^\]]+)\]\(([^)]+)\)\./g;
  result = result.replace(markdownLinkWithPeriodRegex, (match, linkText, url) => {
    console.log('Found markdown link with period:', { match, linkText, url });
    const cleanUrl = url.trim();
    return `<a href="${cleanUrl}" target="_blank" style="color:#E63946; font-weight:600; text-decoration:underline;">${linkText}</a>.`;
  });
  
  console.log('Converted text:', JSON.stringify(result));
  return result;
}

export default function AIAssistant() {
  // Define the dark red color palette for the chatbot
  const colors = {
    primaryDarkRed: '#4A0000', // Main dark red for backgrounds
    accentRed: '#8B0000',      // Darker red for accents (buttons, highlights)
    accentRedDarker: '#6A0000', // Even darker red for hover states
    lightRedTint: '#FFF5F5',   // Very light red for backgrounds
    aiBubbleLightRed: '#FEE',  // Light red for AI chat bubbles
    lighterRedBorder: '#F0DCDC', // Lighter red for borders
    disabledRed: '#DDAAAA',    // Desaturated red for disabled states
  };
  const [messages, setMessages] = useState([
    { from: 'ai', text: "Hi, welcome to Redbox. I’m Rob. Thanks for visiting. What’s the main area of your business you’re looking to improve or automate right now?" },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(generateSessionId()); // Generate once per session

  // Ref for the chat messages container to enable auto-scrolling
  const messagesEndRef = useRef(null);

  // Handler for restarting the chat
  const handleRestartChat = () => {
    setMessages([
      { from: 'ai', text: "Hi, welcome to Redbox. I’m Rob. Thanks for visiting. What’s the main area of your business you’re looking to improve or automate right now?" },
    ]);
    setInput('');
    setSessionId(generateSessionId()); // Regenerate session ID
  };

  // Effect to scroll to the bottom of the chat when messages change or load
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages, loading]); // Depend on messages and loading state

  const sendMessage = async () => {
    // Disable sending if input is empty or if a message is already loading
    if (!input.trim() || loading) return;

    const userMessage = { from: 'user', text: input };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages); // Update messages immediately with user's input
    setInput(''); // Clear input field
    setLoading(true); // Show loading indicator

    try {
      const now = new Date();
      // Format: Monday, June 10, 2024, 3:45 PM GMT+1
      const humanDate = now.toLocaleString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short',
      });
      const response = await axios.post('https://redboxrob.app.n8n.cloud/webhook/9f284ea6-a5a7-41f4-8cfa-557cff2d9576/chat', {
        sessionId,
        action: "sendMessage",
        chatInput: userMessage.text, // Use the user's message text
        datetime: now.toISOString(), // ISO string for reference
        humanDate, // Human-readable date with timezone
      });

      let aiReply = response.data.reply || JSON.stringify(response.data);

      // Check if aiReply is a stringified JSON object and remove outer curly braces
      if (typeof aiReply === 'string' && aiReply.startsWith('{') && aiReply.endsWith('}')) {
        try {
          const parsed = JSON.parse(aiReply);
          // If it's a valid JSON object, and we only want the 'reply' or 'output' part
          // assuming the structure is { "reply": "..." } or { "output": "..." }
          // If it's just a plain string inside, we remove the braces.
          if (parsed.reply) {
            aiReply = parsed.reply;
          } else if (parsed.output) { // Added to handle the specific output format you showed
            aiReply = parsed.output;
          } else {
            // If it's a JSON object but doesn't have 'reply' or 'output', keep it as is,
            // or modify this logic based on expected JSON structure.
            // For now, if it's a simple string inside braces, remove them.
            aiReply = aiReply.substring(1, aiReply.length - 1);
          }
        } catch (e) {
          // Not a valid JSON, treat as a regular string and proceed
          console.warn("Failed to parse AI reply as JSON, treating as plain string:", aiReply);
        }
      }

      // Split the AI reply by '\n' to create multiple bubbles
      const replyParts = aiReply.split('\n').filter(part => part.trim() !== ''); // Filter out empty strings

      setMessages((prevMessages) => {
        let updatedMessages = [...prevMessages];
        replyParts.forEach(part => {
          updatedMessages.push({ from: 'ai', text: part.trim() });
        });
        return updatedMessages;
      });

    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prevMessages) => [...prevMessages, { from: 'ai', text: 'Sorry, something went wrong. Please try again later.' }]);
    } finally {
      setLoading(false); // Hide loading indicator regardless of success or failure
    }
  };

  // Determine if the send button should be disabled
  const isSendButtonDisabled = !input.trim() || loading;

  return (
    <>
      <style>
        {`
          .chat-message a {
            color: #E63946 !important;
            font-weight: 600 !important;
            text-decoration: underline !important;
            transition: all 0.2s ease !important;
          }
          .chat-message a:hover {
            color: #D32F2F !important;
            text-decoration: none !important;
          }
          .chat-message a:visited {
            color: #C62828 !important;
          }
        `}
      </style>
      <div className="flex flex-col h-[450px] md:h-[550px] bg-white rounded-xl shadow-lg border overflow-hidden" style={{ borderColor: colors.lighterRedBorder, background: '#fff' }}>
      {/* Chat Header */}
      <div className="flex items-center p-4 border-b" style={{ borderColor: colors.lighterRedBorder, background: colors.primaryDarkRed }}>
        {/* AI Avatar */}
        <div className="flex items-center justify-center w-8 h-8 rounded-full mr-2" style={{ background: 'transparent', padding: 0 }}>
          <svg width="28" height="28" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="2" width="32" height="32" rx="8" fill="white"/>
            <path d="M10 10H18C19.1046 10 20 10.8954 20 12V16C20 17.1046 19.1046 18 18 18H10V10Z" fill="#4A0000"/>
            <path d="M10 20H22C23.1046 20 24 20.8954 24 22V24C24 25.1046 23.1046 26 22 26H10V20Z" fill="#4A0000"/>
          </svg>
        </div>
        <div className="flex-1">
          <h3 className="text-base font-semibold" style={{ color: colors.lightRedTint, fontSize: '1rem' }}>Website AI Assistant</h3>
          <p className="text-xs" style={{ color: '#FFD600', fontSize: '0.8rem' }}>Online</p>
        </div>
        {/* Close Button (Example - you might want to add functionality) */}
        <button className="text-gray-400 hover:text-gray-600 focus:outline-none" style={{ color: colors.disabledRed }} onClick={handleRestartChat}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      {/* Chat Messages */}
      <div
        ref={messagesEndRef} // Attach ref here for scrolling
        className="flex-1 overflow-y-auto p-3 space-y-3 scroll-smooth"
      >
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.from === 'ai' ? 'justify-start' : 'justify-end'}`}>
            <div
              style={
                msg.from === 'user'
                  ? {
                      background: colors.accentRed,
                      color: colors.lightRedTint,
                      borderRadius: '1rem 1rem 0.25rem 1rem',
                      boxShadow: '0 1px 4px rgba(138,0,0,0.08)',
                      fontSize: '0.95rem',
                      padding: '0.6rem 1rem',
                    }
                  : {
                      background: colors.aiBubbleLightRed,
                      color: colors.primaryDarkRed,
                      borderRadius: '1rem 1rem 1rem 0.25rem',
                      boxShadow: '0 1px 4px rgba(138,0,0,0.06)',
                      fontSize: '0.95rem',
                      padding: '0.6rem 1rem',
                    }
              }
              className="max-w-[75%] shadow-sm chat-message"
              dangerouslySetInnerHTML={{
                __html: convertMarkdownLinks(msg.text)
              }}
            />
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="max-w-[75%] p-2 rounded-xl shadow-sm rounded-bl-none" style={{ background: colors.aiBubbleLightRed, color: colors.primaryDarkRed, fontSize: '0.95rem' }}>
              <span className="inline-block w-2 h-2 rounded-full animate-pulse mr-1" style={{ background: colors.primaryDarkRed }}></span>
              <span className="inline-block w-2 h-2 rounded-full animate-pulse delay-75 mr-1" style={{ background: colors.primaryDarkRed }}></span>
              <span className="inline-block w-2 h-2 rounded-full animate-pulse delay-150" style={{ background: colors.primaryDarkRed }}></span>
            </div>
          </div>
        )}
      </div>

      {/* Chat Input */}
      <div className="flex items-center p-3 border-t bg-white" style={{ borderColor: colors.lighterRedBorder }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
          placeholder="Type your message..."
          className="flex-1 rounded-full border transition-all duration-200 mr-2 text-gray-800 placeholder-gray-400"
          style={{
            borderColor: colors.lighterRedBorder,
            color: colors.primaryDarkRed,
            background: colors.lightRedTint,
            outline: 'none',
            fontSize: '0.95rem',
            padding: '0.5rem 1rem',
          }}
        />
        <button
          onClick={sendMessage}
          disabled={isSendButtonDisabled}
          className="rounded-full transition-all duration-200"
          style={{
            background: isSendButtonDisabled ? colors.disabledRed : colors.accentRed,
            cursor: isSendButtonDisabled ? 'not-allowed' : 'pointer',
            color: colors.lightRedTint,
            outline: 'none',
            border: 'none',
            boxShadow: isSendButtonDisabled ? 'none' : `0 2px 8px ${colors.accentRedDarker}22`,
            padding: '0.5rem',
          }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
          </svg>
        </button>
      </div>
    </div>
    </>
  );
}
